// One world on the Portal (Ben, Oct 19 2026): the summary row, and — opened — the cities with empty queues and the
// quests to claim there. Closed by default; open/closed is remembered on this device ("portal.world.<wid>").
import type { QuestItem } from "@/api/types";
import { art } from "@/kit/art";
import { IconChevronDown, IconChevronRight, IconHourglass, IconMail, IconScroll } from "@/kit/icons";
import { Bar, Button, Disc, Medallion, Row, RowText } from "@/kit/primitives";
import { useRememberedOpen } from "@/state/collapse";
import { fmtDuration } from "@/game/time";
import { fmtNum, plural } from "@/game/format";
import type { WorldGlance } from "./usePortal";
import { useWorldQueues } from "./useWorldQueues";

const cx = (...a: (string | false | null | undefined)[]) => a.filter(Boolean).join(" ");

export function WorldRow({ g, now, claiming, onClaim, onGo, onCity, onQuests }: {
  g: WorldGlance; now: number; claiming: string | null;
  onClaim: (g: WorldGlance, q: QuestItem) => Promise<void>; onGo: (g: WorldGlance) => void; onCity: (vid: number) => void; onQuests: () => void;
}) {
  const [open, toggle] = useRememberedOpen(`portal.world.${g.wid}`);
  const q = useWorldQueues(g.wid, g.vids, open, now);
  const claim = g.claimable.length;
  const attacked = g.incoming > 0;
  const inMs = g.firstLandMs ? g.firstLandMs - now : null;

  const bits: React.ReactNode[] = [];
  if (attacked) bits.push(<span key="a" className="r">Under attack · {fmtNum(g.incoming)} incoming</span>);
  if (claim) bits.push(<span key="q"><b>{claim}</b> {claim === 1 ? "quest" : "quests"} to claim</span>);
  if (q.complete && q.idle.length) bits.push(<span key="i"><b>{q.idle.length}</b> {q.idle.length === 1 ? "city" : "cities"} idle</span>);
  if (!attacked && g.newReport) bits.push(<span key="r">new reports</span>);
  if (!attacked && g.newMail) bits.push(<span key="m">new mail</span>);
  if (!bits.length) bits.push(<span key="z" className="quiet"><span className="dot" />All quiet</span>);

  const claimAll = async () => {
    for (const x of g.claimable) await onClaim(g, x);
  };

  return (
    <div className={cx("pw", open && "open")}>
      <Row alert={attacked} className="pw-head" onClick={(e) => { if (!(e.target as HTMLElement).closest("button")) toggle(); }}>
        <Medallion src={attacked ? art.icon.shield : undefined} alt="" glow={attacked ? "crim" : false}>
          {!attacked && (claim ? <img src={art.nav.quests} alt="" /> : g.newMail && !g.newReport ? <IconMail /> : g.queueEmpty ? <IconHourglass /> : <IconScroll />)}
        </Medallion>
        <RowText
          eyebrow={`World ${g.wid}${g.name ? ` · ${g.name}` : ""}`}
          line={bits.map((b, i) => <span key={i}>{i > 0 ? " · " : null}{b}</span>)}
          meta={attacked && inMs !== null ? `First lands in ${fmtDuration(inMs)} · ${plural(g.cities, "city", "cities")}` : plural(g.cities, "city", "cities")}
        />
        <div className="right">
          {attacked
            ? <Button kind="crimson" size="md" badge={g.incoming} onClick={() => onGo(g)}>Defend</Button>
            : <Button size="md" onClick={() => onGo(g)}>Go to</Button>}
          <Disc size="xs" label={open ? "Collapse" : "Expand"} aria-expanded={open} onClick={toggle}>
            {open ? <IconChevronDown /> : <IconChevronRight />}
          </Disc>
        </div>
      </Row>

      {open && (
        <div className="pw-body">
          <div className="pw-sec">
            <div className="pw-sh">
              <span>Empty queues</span>
              <span className="aside">{q.loading ? `Checking ${q.read} of ${q.total}` : `${q.idle.length} of ${plural(q.total, "city", "cities")}`}</span>
            </div>
            {q.loading && <Bar value={q.total ? q.read / q.total : 0} color="gold" />}
            {q.idle.map((c) => (
              <Row key={c.vid} as="button" sub className="pw-city" onClick={() => onCity(c.vid)}>
                <div className="txt">
                  <div className="line"><b className="cp-name">{c.name}</b> <span className="xy">({c.coords})</span></div>
                  <div className="pw-idle">{c.idle.map((t) => <span key={t} className="pw-chip">{t}</span>)}</div>
                </div>
                <IconChevronRight className="chev" />
              </Row>
            ))}
            {!q.loading && !q.idle.length && !q.failed && <div className="pw-none">Every queue is busy in {q.total === 1 ? "your city" : `all ${q.total} cities`}.</div>}
            {!q.loading && q.failed > 0 && <div className="pw-none">{q.failed === 1 ? "One city" : `${q.failed} cities`} couldn't be read. Open the world to check {q.failed === 1 ? "it" : "them"}.</div>}
          </div>

          <div className="pw-sec">
            <div className="pw-sh">
              <span>Quests to claim</span>
              {claim > 0 && <span className="badge inline">{claim}</span>}
            </div>
            <div className="pw-quests">
              <span className="pw-qtxt">{claim ? <><b>{claim}</b> {claim === 1 ? "reward is" : "rewards are"} ready in World {g.wid}</> : "Nothing to claim right now"}</span>
              <div className="pw-qbtns">
                <Button size="sm" onClick={onQuests}>Quests</Button>
                {claim > 0 && <Button kind="gold" size="sm" busy={!!claiming && claiming.startsWith(`${g.wid}:`)} onClick={claimAll}>Claim all</Button>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
